import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Divider, Stack } from "@mui/material";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsSingleSelect, { OptionsValueType } from "components/settingsConfig/uiComponents/SettingsSingleSelect";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, WidgetRequest } from "system/widget/Widget";
import { QueryInfo } from "system/query/QueryInfo";
import { socialInfluencersDefaultSettings, SocialInfluencersSettingsStateType } from "./SocialInfluencersUtils";

const defaultWidgetRequest = {
  name: "social-influencers",
  endpoint: "query/social-influencers",
  queryParams: {},
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE,
} as WidgetRequest;

function buildSocialInfluencersRequest(settingsState: SocialInfluencersSettingsStateType): WidgetRequest {
  return {
    ...defaultWidgetRequest,
    requestInit: {
      body: {
        options: {
          size: settingsState.size,
          min_hits: settingsState.minHits,
        },
      },
    },
  };
}

type LegacyRequestBody = {
  options?: {
    size?: number;
    min_hits?: number;
  };
};

// Compatibility layer to support legacy widgets that stored settings in different places
function hydrateSettingsState(widgetSettings?: Partial<SocialInfluencersSettingsStateType>, requestBody?: LegacyRequestBody): SocialInfluencersSettingsStateType {
  const requestOptions = requestBody?.options;

  return {
    // Start with defaults
    ...socialInfluencersDefaultSettings,

    // Existing persisted settings
    ...widgetSettings,

    // Legacy request-backed settings compatibility
    size: widgetSettings?.size ?? requestOptions?.size ?? socialInfluencersDefaultSettings.size,
    minHits: widgetSettings?.minHits ?? requestOptions?.min_hits ?? socialInfluencersDefaultSettings.minHits,
    pageSize: widgetSettings?.pageSize ?? socialInfluencersDefaultSettings.pageSize,
  };
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function BuildDefaultSocialInfluencersListRequests(queryInfo: QueryInfo): WidgetRequest[] | undefined {
  return [buildSocialInfluencersRequest(socialInfluencersDefaultSettings)];
}

export default function SocialInfluencersListSettings(props: WidgetSettingsProps): React.JSX.Element {
  const { i18n } = useTranslation();

  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, "social-influencers");
  const requestBody = originalRequest?.requestInit?.body;

  const [settingsState, setSettingsState] = useState<SocialInfluencersSettingsStateType>(
    hydrateSettingsState(props.widget?.data?.settings, requestBody)
  );
  const settingsOnChange = (updatedState: SocialInfluencersSettingsStateType) => setSettingsState(updatedState);

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  const minHitsOptions: OptionsValueType[] = useMemo(() => 
    [1, 2, 3, 4, 5, 10, 25, 50, 100, 250, 500, 1000].map((value) => ({
      label: String(value),
      value,
    })), []);

  const sizeOptions: OptionsValueType[] = useMemo(() =>
    [5, 10, 25, 50, 100, 250, 500, 1000].map((value) => ({
      label: String(value),
      value,
    })), []);

  const pageSizeOptions: OptionsValueType[] = useMemo(() =>
    [5, 10, 20, 50].map((value) => ({
      label: String(value),
      value,
    })), []);

  const displayValueOptions: OptionsValueType[] = useMemo(() => [
    { label: i18n.t("avg"), value: "average" },
    { label: i18n.t("count"), value: "total" },
  ], []);

  const updateWidget = () => {
    props.saveWidgetDataAndRequests({
      settings: settingsState,
    }, [buildSocialInfluencersRequest(settingsState)]).then(() => {
        if (props.widgetRefresh) {
          props.widgetRefresh();
        }
      });
  };

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      maxWidth="md"
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateWidget();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("widget-settings.query-and-data"), i18n.t("display")]}
      title={i18n.t("widget-types.social-influencers.settings.title")}
    >
      <>
        {tabValue === 0 && (
          <SplitSettingsWrapper>
            <SettingsOptions settingsState={settingsState} onChange={settingsOnChange}>
                <SettingsSingleSelect
                  keyid="minHits"
                  label={i18n.t("widget-types.social-influencers.settings.min-records")}
                  labelPosition="top"
                  options={minHitsOptions}
                  value={minHitsOptions.find((option) => option.value === settingsState.minHits) ?? minHitsOptions[0]}
                  defaultValue={minHitsOptions[0]}
                />

                <SettingsSingleSelect
                  keyid="size"
                  label={i18n.t("widget-types.social-influencers.settings.size")} 
                  labelPosition="top"
                  options={sizeOptions}
                  value={sizeOptions.find((option) => option.value === settingsState.size) ?? sizeOptions[0]}
                  defaultValue={sizeOptions[0]}
                />
            </SettingsOptions>
          </SplitSettingsWrapper>
        )}

        {tabValue === 1 && (
          <SplitSettingsWrapper>
            <SettingsOptions settingsState={settingsState} onChange={settingsOnChange}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider={true}>
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                  {settingsState.widgetTitle === 'custom' &&
                    <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                      <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                      <SettingsTextField
                        keyid="customWidgetTitle"
                        label={i18n.t("widget-settings.custom-text")}
                        value={settingsState.customWidgetTitle}
                      />
                    </Stack>
                  }
                <Box sx={{ mt: 2 }}>
                  <SettingsToggleButton
                    keyid="widgetTitleAlignment"
                    label={i18n.t("widget-settings.alignment")}
                    optionsList={horizontalAlignmentOptions}
                    value={settingsState.widgetTitleAlignment} />
                </Box>
              </SettingsAccordion>

              <SettingsAccordion label={i18n.t("display")}>
                {/* Page Size */}
                <SettingsSingleSelect
                  keyid="pageSize"
                  label={i18n.t("widget-types.social-influencers.settings.page-size")}
                  options={pageSizeOptions}
                  value={pageSizeOptions.find((option) => option.value === settingsState.pageSize) ?? pageSizeOptions[0]}
                  defaultValue={pageSizeOptions[0]}
                />

                {/* Display Value */}
                <SettingsSingleSelect
                  keyid="displayValue"
                  label={i18n.t("widget-types.social-influencers.settings.display-records")}
                  options={displayValueOptions}
                  value={displayValueOptions.find((option) => option.value === settingsState.displayValue) ?? displayValueOptions[0]}
                  defaultValue={displayValueOptions[0]}
                />
              </SettingsAccordion>
            </SettingsOptions>
          </SplitSettingsWrapper>
        )}
      </>
    </TabbedDialog>
  );
}